import React, {useEffect, useMemo, useState} from 'react';
import {useQuery, useMutation} from '@apollo/client';
import {
    NavBar,
    Card,
    Collapse,
    Skeleton,
    TextArea,
    Button,
    Dialog,
    Toast,
    Space,
    Image,
    ImageViewer,
} from 'antd-mobile';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {getNavBarContainerStyle} from '../../../../utils/deviceUtils';
import PhotoSelector from '../../../delivery/components/PhotoSelector';
import MultiPhotoGalleryView from '../../../delivery/components/MultiPhotoGalleryView';
import MultiPhotoSelectorView from '../../../delivery/components/MultiPhotoSelectorView';
import {isNativeWebView, openCamera} from '../../../../utils/appRuntime';
import {base64ToFile} from '../../../../services/DeliveryDataService';
import {fileUpload} from '../../../../utils/storageUtils';
import {showError} from '../../../../utils/exceptionUtils';
import {
    GET_DSP_COMPLAINT_DETAIL,
    GetDspComplaintDetailResponse,
    GetDspComplaintDetailVariables,
} from './graphql/queries/getDspComplaintDetail';
import {
    ABANDON_COMPLAINT_APPEAL,
    AbandonComplaintAppealResponse,
    AbandonComplaintAppealVariables,
    ACKNOWLEDGE_COMPLAINT,
    AcknowledgeComplaintResponse,
    AcknowledgeComplaintVariables,
    SUBMIT_COMPLAINT_APPEAL,
    SubmitComplaintAppealResponse,
    SubmitComplaintAppealVariables,
} from './graphql/mutations/complaintMutations';
import {ComplaintStatus} from './graphql/types/CustomerComplaintType';
import {normalizeLang, pickDisplayText, renderReviewComment} from '../../../../utils/i18nText';
import RichTextView from '../../../../components/RichTextView';
import {useComplaintStatusText} from './hooks/useComplaintStatusText';
import {formatTime, isImageUrl, isOverdue} from './utils/complaintListUtils';
import {resolveComplaintSourceFilter} from './utils/complaintSource';

const APPEAL_REMARK_MIN = 1;
const APPEAL_REMARK_MAX = 500;
const MIN_APPEAL_IMAGES = 1;
const MAX_APPEAL_IMAGES = 3;
const MAX_APPEAL_IMAGE_MB = 20;

type AppealPhotoStatus = 'uploading' | 'done' | 'error';

/**
 * 申诉凭证图片（选时预上传）：选中即异步上传到 S3，提交时直接取 objectKey。
 * status=uploading 时禁用提交，error 项可重传。
 */
interface AppealPhoto {
    uid: string;
    file: File;
    objectKey?: string;
    status: AppealPhotoStatus;
}

let appealPhotoUidSeq = 0;
const nextAppealPhotoUid = (): string => `appeal-photo-${++appealPhotoUidSeq}`;

/** 渲染后端结构化 i18n 文本为当前 locale 下的展示字符串；空值返回 '-' */
const renderLocalizedText = (
    parsed: {text: string; lang: string; translations: Array<{lang: string; text: string}>} | null | undefined,
    locale: string,
): string => pickDisplayText(parsed, normalizeLang(locale)) || '-';

/**
 * 客诉详情展示（对齐审核侧）：默认按当前 locale 显示译文，存在真实译文时提供「显示原文/译文」切换；
 * 文本可能含外部富文本，经 RichTextView（DOMPurify 净化）渲染，防存储型 XSS。
 */
const ComplaintReasonView: React.FC<{
    reason: {text: string; lang: string; translations: Array<{lang: string; text: string}>};
    currentLocale: string;
    label: string;
    showOriginalLabel: string;
    showTranslationLabel: string;
}> = ({reason, currentLocale, label, showOriginalLabel, showTranslationLabel}) => {
    const [showOriginal, setShowOriginal] = useState(false);
    const translated = pickDisplayText(reason, currentLocale);
    const original = reason.text || '';
    const hasTranslation = !!reason.translations?.find((e) => e.lang === currentLocale)?.text;
    const hasDistinctOriginal = hasTranslation && !!original;
    const displayText = showOriginal ? original : translated;

    return (
        <div>
            <div className="flex items-center justify-between">
                <span className="text-gray-500">{label}：</span>
                {hasDistinctOriginal && (
                    <Button
                        fill="none"
                        size="mini"
                        className="px-0"
                        style={{color: '#1677ff'}}
                        onClick={() => setShowOriginal((v) => !v)}
                    >
                        {showOriginal ? showTranslationLabel : showOriginalLabel}
                    </Button>
                )}
            </div>
            <RichTextView
                html={displayText}
                className="text-black text-sm mt-1 p-3 [&_img]:max-w-full"
                style={{background: '#fafafa', borderRadius: 4, whiteSpace: 'pre-wrap'}}
            />
        </div>
    );
};


/**
 * 客诉下发节点的操作人 i18n key 与默认文案：复用聚合规则
 *   - 客服（CUSTOMER_SERVICE / EMAIL/PHONE）→ customerService
 *   - 系统（SYSTEM / OTHER / 未识别）       → system
 */
const resolveComplaintSourceOperator = (source?: string): {key: string; defaultLabel: string} => {
    const filter = resolveComplaintSourceFilter(source);
    if (filter === 'CUSTOMER_SERVICE') {
        return {
            key: 'pages.dsp.home.customerComplaint.detail.timeline.operator.customerService',
            defaultLabel: '客服',
        };
    }
    return {
        key: 'pages.dsp.home.customerComplaint.detail.timeline.operator.system',
        defaultLabel: '系统',
    };
};

interface TimelineNode {
    color: string;
    time: string;
    title: string;
    description?: string;
    /** 用于排序的时间戳（epoch seconds），不展示 */
    ts: number;
}

const CustomerComplaintDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const {t, i18n} = useTranslation();
    const {getStatusText} = useComplaintStatusText();
    const {complaintId} = useParams<{complaintId: string}>();
    const numericId = complaintId ? Number(complaintId) : 0;

    const [remark, setRemark] = useState('');
    const [selectedPhotos, setSelectedPhotos] = useState<AppealPhoto[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [imageViewer, setImageViewer] = useState<{visible: boolean; images: string[]; index: number}>({
        visible: false,
        images: [],
        index: 0,
    });

    const photoPreviewUrls = useMemo(
        () => selectedPhotos.map((p) => URL.createObjectURL(p.file)),
        [selectedPhotos],
    );
    // 预览 blob URL 在列表变化/组件卸载时回收，避免内存泄漏
    useEffect(() => {
        return () => {
            photoPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [photoPreviewUrls]);

    const {data: detailResp, loading: complaintLoading, refetch: refetchComplaint} = useQuery<
        GetDspComplaintDetailResponse,
        GetDspComplaintDetailVariables
    >(GET_DSP_COMPLAINT_DETAIL, {
        variables: {complaintId: numericId},
        skip: !numericId,
        fetchPolicy: 'no-cache',
    });

    const detail = detailResp?.getDspComplaintDetail;
    const complaint = detail?.complaint;
    const relatedPackage = detail?.relatedPackage;
    const appealDetail = detail?.appealDetail;
    const appealLoading = complaintLoading;
    const isPending = complaint?.status === ComplaintStatus.PENDING_DSP_ACTION;
    const isAppealing = complaint?.status === ComplaintStatus.APPEALING;
    // 前端兜底：已过申诉截止但定时任务尚未把 status 流转为 TIMEOUT 时，禁止提交（后端仍会校验）
    const isAppealExpired = isOverdue(complaint?.appealDeadlineAt);

    const podImageUrls = relatedPackage?.podImageS3UrlsJson ?? relatedPackage?.podImageS3KeysJson ?? [];
    const deliveredDriverName = relatedPackage?.deliveredDriverName;

    const [submitAppeal] = useMutation<SubmitComplaintAppealResponse, SubmitComplaintAppealVariables>(
        SUBMIT_COMPLAINT_APPEAL,
    );
    const [acknowledge] = useMutation<AcknowledgeComplaintResponse, AcknowledgeComplaintVariables>(
        ACKNOWLEDGE_COMPLAINT,
    );
    const [abandon] = useMutation<AbandonComplaintAppealResponse, AbandonComplaintAppealVariables>(
        ABANDON_COMPLAINT_APPEAL,
    );

    const hasUploading = selectedPhotos.some((p) => p.status === 'uploading');
    const hasUploadError = selectedPhotos.some((p) => p.status === 'error');

    // 选时预上传：置入 uploading 占位，异步上传 S3，成功写 objectKey/done，失败置 error（可重传）
    const startUpload = (uid: string, file: File) => {
        const tag = complaint?.externalRefId ?? '';
        fileUpload(file, tag)
            .then(({objectKey}) => {
                setSelectedPhotos((prev) =>
                    prev.map((p) => (p.uid === uid ? {...p, objectKey, status: 'done'} : p)),
                );
            })
            .catch(() => {
                setSelectedPhotos((prev) =>
                    prev.map((p) => (p.uid === uid ? {...p, status: 'error'} : p)),
                );
            });
    };

    // 失败项重传：重置为 uploading 后再次上传
    const retryUpload = (photo: AppealPhoto) => {
        setSelectedPhotos((prev) =>
            prev.map((p) => (p.uid === photo.uid ? {...p, status: 'uploading'} : p)),
        );
        startUpload(photo.uid, photo.file);
    };

    const handlePhotoSelected = (file: File) => {
        if (file.size > MAX_APPEAL_IMAGE_MB * 1024 * 1024) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.imageTooLarge',
                    '图片大小不能超过 {{max}}MB',
                    {max: MAX_APPEAL_IMAGE_MB},
                ) as string,
            });
            return;
        }
        if (selectedPhotos.length >= MAX_APPEAL_IMAGES) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.maxImagesReached',
                    '最多上传 {{n}} 张',
                    {n: MAX_APPEAL_IMAGES},
                ) as string,
            });
            return;
        }
        const uid = nextAppealPhotoUid();
        setSelectedPhotos((prev) => [...prev, {uid, file, status: 'uploading'}]);
        startUpload(uid, file);
    };

    const handleRemovePhoto = (idx: number) => {
        setSelectedPhotos((prev) => prev.filter((_, i) => i !== idx));
    };

    // 原生端：接收 base64 数组（来自 openCamera 或 openPhotoPicker），转 File 后追加
    const handleNativePhotosSelected = async (base64Photos: string[]) => {
        if (!base64Photos?.length) return;
        try {
            const files = await Promise.all(
                base64Photos.map((b64, i) =>
                    base64ToFile(b64, `appeal_${Date.now()}_${i}.jpg`),
                ),
            );
            const room = Math.max(0, MAX_APPEAL_IMAGES - selectedPhotos.length);
            if (files.length > room) {
                Toast.show({
                    content: t(
                        'pages.dsp.home.customerComplaint.detail.maxImagesReached',
                        '最多上传 {{n}} 张',
                        {n: MAX_APPEAL_IMAGES},
                    ) as string,
                });
            }
            const accepted = files.slice(0, room).map((file) => ({
                uid: nextAppealPhotoUid(),
                file,
                status: 'uploading' as const,
            }));
            setSelectedPhotos((prev) => [...prev, ...accepted]);
            accepted.forEach((p) => startUpload(p.uid, p.file)); // 选时即预上传
        } catch (e) {
            Toast.show({content: (e as Error)?.message ?? '处理图片失败'});
        }
    };

    const handleNativeBurstCapture = () => {
        const remaining = Math.max(0, MAX_APPEAL_IMAGES - selectedPhotos.length);
        if (remaining === 0) return;
        openCamera(remaining, 0, '', handleNativePhotosSelected);
    };

    const handleSubmitAppeal = async () => {
        // 已过申诉截止：前端兜底拦截（按钮已 disabled，此处防御）
        if (isAppealExpired) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.appealExpired',
                    '已超时,无法申诉',
                ) as string,
            });
            return;
        }
        // 申诉理由按原文长度校验：1-500 字符必填
        const trimmedRemark = remark.trim();
        if (trimmedRemark.length < APPEAL_REMARK_MIN || trimmedRemark.length > APPEAL_REMARK_MAX) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.appealReasonLength',
                    '申诉理由需 {{min}}-{{max}} 字',
                    {min: APPEAL_REMARK_MIN, max: APPEAL_REMARK_MAX},
                ) as string,
            });
            return;
        }
        if (selectedPhotos.length < MIN_APPEAL_IMAGES) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.atLeastImages',
                    '至少上传 {{n}} 张凭证',
                    {n: MIN_APPEAL_IMAGES},
                ) as string,
            });
            return;
        }
        // 上传中 / 存在失败项时不允许提交（按钮已 disabled，此处兜底）
        if (hasUploading) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.attachmentUploading',
                    '凭证上传中，请稍候',
                ) as string,
            });
            return;
        }
        if (hasUploadError) {
            Toast.show({
                content: t(
                    'pages.dsp.home.customerComplaint.detail.attachmentUploadFailed',
                    '存在上传失败的凭证，请重传或删除后再提交',
                ) as string,
            });
            return;
        }
        try {
            setSubmitting(true);
            // 预上传已拿到 objectKey，提交直接收集
            const s3Keys = selectedPhotos.map((p) => p.objectKey!);
            await submitAppeal({
                variables: {
                    complaintId: numericId,
                    appealReason: trimmedRemark,
                    appealAttachmentS3KeysJson: JSON.stringify(s3Keys),
                },
            });
            Toast.show({content: t('pages.dsp.home.customerComplaint.detail.submitSuccess', '申诉已提交')});
            await refetchComplaint();
            navigate(-1);
        } catch (e: any) {
            // 解析后端 BusinessException 的 messageKey 走 i18n（如申诉超时 appealSlaExpired），避免裸 key
            showError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAcknowledge = async () => {
        const ok = await Dialog.confirm({
            title: t('pages.dsp.home.customerComplaint.detail.abandonConfirmTitle', '确认放弃申诉？'),
            content: t('pages.dsp.home.customerComplaint.detail.acknowledgeConfirm', '若放弃申诉，即认责该问题包裹系 DSP 派送问题导致，同意承担后续相关罚款'),
            confirmText: t('pda.common.confirm'),
            cancelText: t('pda.common.cancel'),
        });
        if (!ok) return;
        try {
            await acknowledge({variables: {complaintId: numericId}});
            Toast.show({content: t('pages.dsp.home.customerComplaint.detail.acknowledgeSuccess', '已放弃')});
            navigate(-1);
        } catch (e: any) {
            showError(e);
        }
    };

    const handleAbandon = async () => {
        const ok = await Dialog.confirm({
            title: t('pages.dsp.home.customerComplaint.detail.abandonConfirmTitle', '确认放弃申诉？'),
            content: t('pages.dsp.home.customerComplaint.detail.abandonConfirm', '若放弃申诉，即认责该问题包裹系 DSP 派送问题导致，同意承担后续相关罚款'),
            confirmText: t('pda.common.confirm'),
            cancelText: t('pda.common.cancel'),
        });
        if (!ok) return;
        try {
            await abandon({variables: {complaintId: numericId}});
            Toast.show({content: t('pages.dsp.home.customerComplaint.detail.abandonSuccess', '已放弃申诉')});
            navigate(-1);
        } catch (e: any) {
            showError(e);
        }
    };

    // mobile 一期附件回显仅支持图片：PC 端可提交视频/压缩包等非图片附件，mobile 用 <Image> 渲染会破图，故过滤只留图片
    const attachmentUrls = (appealDetail?.appealAttachmentS3UrlsJson ?? []).filter(isImageUrl);

    const timelineNodes: TimelineNode[] = useMemo(() => {
        if (!complaint) return [];
        const nodes: TimelineNode[] = [];
        if (complaint.createdAt) {
            const sourceOperator = resolveComplaintSourceOperator(complaint.source);
            const operatorLabel = t(sourceOperator.key, sourceOperator.defaultLabel);
            nodes.push({
                color: '#52c41a',
                time: formatTime(complaint.createdAt) ?? '',
                title: t('pages.dsp.home.customerComplaint.detail.timeline.created', '上报问题件'),
                description: `${t(
                    'pages.dsp.home.customerComplaint.detail.timeline.operatorLabel',
                    '操作人',
                )}：${operatorLabel}`,
                ts: complaint.createdAt,
            });
        }
        if (appealDetail?.appealSubmittedAt) {
            nodes.push({
                color: '#1677ff',
                time: formatTime(appealDetail.appealSubmittedAt) ?? '',
                title: t('pages.dsp.home.customerComplaint.detail.timeline.appealSubmitted', 'DSP 提交申诉'),
                // 不带申诉理由描述，申诉详情区已展示
                ts: appealDetail.appealSubmittedAt,
            });
        }
        if (appealDetail?.reviewedAt && appealDetail.reviewResult) {
            const resultLabel = t(
                `pages.dsp.home.customerComplaint.reviewResult.${appealDetail.reviewResult}`,
                appealDetail.reviewResult,
            );
            nodes.push({
                color: appealDetail.reviewResult === 'APPROVED' ? '#52c41a' : '#ff4d4f',
                time: formatTime(appealDetail.reviewedAt) ?? '',
                title: `${t(
                    'pages.dsp.home.customerComplaint.detail.timeline.reviewResult',
                    '审核结果',
                )}：${resultLabel}`,
                ts: appealDetail.reviewedAt,
            });
        }
        if (complaint.isFinished === 1 && complaint.finishedAt && !appealDetail?.reviewedAt) {
            // 完结节点（主动认责 ACKNOWLEDGED / 超时认责 TIMEOUT 等）：展示操作人。
            // operatorName 来自后端 updatedBy 解析：为空表示系统（超时认责=系统，或账号查不到），兜底「系统」文案。
            const operatorLabel = complaint.operatorName
                || t('pages.dsp.home.customerComplaint.detail.timeline.operator.system', '系统');
            nodes.push({
                color: '#999',
                time: formatTime(complaint.finishedAt) ?? '',
                title: getStatusText(complaint.status),
                description: `${t(
                    'pages.dsp.home.customerComplaint.detail.timeline.operatorLabel',
                    '操作人',
                )}：${operatorLabel}`,
                ts: complaint.finishedAt,
            });
        }
        return nodes.sort((a, b) => b.ts - a.ts);
    }, [complaint, appealDetail, t, getStatusText]);

    const violationReasonLabel = complaint
        ? (t(
              `pages.dsp.home.customerComplaint.complaintType.${complaint.complaintType}`,
              complaint.complaintType,
          ) as string)
        : '';

    return (
        <div className="bg-gray-50 min-h-screen pb-24">
            <div {...getNavBarContainerStyle()} className="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
                <NavBar onBack={() => navigate(-1)} backIcon={<ArrowLeftOutlined />}>
                    {t('pages.dsp.home.customerComplaint.detail.title', '详情')}
                </NavBar>
            </div>

            <div className="pt-14 px-3 space-y-3">
                {complaintLoading ? (
                    <Skeleton.Paragraph lineCount={6} animated />
                ) : !complaint ? (
                    <div className="text-center text-gray-500 mt-10">
                        {t('pages.dsp.home.customerComplaint.detail.notFound', '未找到该客诉')}
                    </div>
                ) : (
                    <>
                        <Collapse accordion defaultActiveKey="parcel">
                            <Collapse.Panel
                                key="parcel"
                                title={
                                    <span className="font-bold">
                                        {t('pages.dsp.home.customerComplaint.detail.parcel', '包裹信息')}
                                    </span>
                                }
                            >
                                <div className="text-sm space-y-1">
                                    <div>
                                        <span className="text-gray-500">
                                            {t('pages.dsp.home.customerComplaint.detail.violationReason', '违规原因')}：
                                        </span>
                                        <span className="text-black">{violationReasonLabel}</span>
                                    </div>
                                    {complaint?.complaintReason && (complaint.complaintReason.text || '').length > 0 && (
                                        <div>
                                            <ComplaintReasonView
                                                reason={complaint.complaintReason}
                                                currentLocale={normalizeLang(i18n.language)}
                                                label={t('pages.dsp.home.customerComplaint.detail.complaintDetail', '客诉详情')}
                                                showOriginalLabel={t('pages.dsp.home.customerComplaint.detail.showOriginal', '显示原文')}
                                                showTranslationLabel={t('pages.dsp.home.customerComplaint.detail.showTranslation', '显示译文')}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-gray-500">
                                            {t('pages.dsp.home.customerComplaint.detail.trackingNumber', '运单号')}：
                                        </span>
                                        <span className="text-black">{relatedPackage?.trackingNumber ?? complaint?.trackingNumber ?? '-'}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">
                                            {t('pages.dsp.home.customerComplaint.detail.driver', '投递司机')}：
                                        </span>
                                        <span className="text-black">{deliveredDriverName ?? '-'}</span>
                                    </div>
                                    {podImageUrls.length > 0 && (
                                        <div>
                                            <div className="text-gray-500 mt-1 mb-1">
                                                {t('pages.dsp.home.customerComplaint.detail.pod', 'POD')}：
                                            </div>
                                            <Space wrap>
                                                {podImageUrls.map((url, idx) => (
                                                    <Image
                                                        key={idx}
                                                        src={url}
                                                        width={64}
                                                        height={64}
                                                        fit="cover"
                                                        onClick={() =>
                                                            setImageViewer({visible: true, images: podImageUrls, index: idx})
                                                        }
                                                    />
                                                ))}
                                            </Space>
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-gray-500">
                                            {t('pages.dsp.home.customerComplaint.detail.orderOverallStatus', '包裹状态')}：
                                        </span>
                                        <span className="text-black">
                                            {complaint.orderOverallStatus
                                                ? t(`common.overallStatus.${complaint.orderOverallStatus}`, complaint.orderOverallStatus)
                                                : '-'}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="text-gray-500">
                                            {t('pages.dsp.home.customerComplaint.detail.deadline', '处理时效')}：
                                        </span>
                                        <span className="text-red-500">
                                            {formatTime(complaint.appealDeadlineAt)}
                                            {t(
                                                'pages.dsp.home.customerComplaint.detail.deadlineHint',
                                                '，到期不处理视为放弃',
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </Collapse.Panel>
                            <Collapse.Panel
                                key="timeline"
                                title={
                                    <span className="font-bold">
                                        {t('pages.dsp.home.customerComplaint.detail.timelineTitle', '问题件记录')}
                                    </span>
                                }
                            >
                                {timelineNodes.length === 0 ? (
                                    <div className="text-sm text-gray-500">
                                        {t('pages.dsp.home.customerComplaint.detail.noEvents', '暂无记录')}
                                    </div>
                                ) : (
                                    <div className="text-sm">
                                        {timelineNodes.map((node, index) => (
                                            <div key={index} className="flex pb-3 last:pb-0">
                                                <div className="flex flex-col items-center mr-3">
                                                    <span
                                                        className="block rounded-full"
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            marginTop: 6,
                                                            backgroundColor: node.color,
                                                        }}
                                                    />
                                                    {index < timelineNodes.length - 1 && (
                                                        <span
                                                            className="block flex-1"
                                                            style={{
                                                                width: 1,
                                                                backgroundColor: '#e5e7eb',
                                                                minHeight: 24,
                                                                marginTop: 2,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="text-xs text-gray-500">{node.time}</div>
                                                    <div className="font-medium">{node.title}</div>
                                                    {node.description && (
                                                        <div
                                                            className="text-xs text-gray-600 mt-0.5"
                                                            style={{wordBreak: 'break-all'}}
                                                        >
                                                            {node.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Collapse.Panel>
                        </Collapse>

                        {isPending && (
                            <div className="bg-white rounded-lg shadow-sm p-3">
                                <div className="font-medium mb-1 text-base">
                                    <span className="text-red-500 mr-1">*</span>
                                    {t('pages.dsp.home.customerComplaint.detail.appeal', '申诉')}
                                    <span className="text-xs text-gray-500 ml-1">
                                        ({t(
                                            'pages.dsp.home.customerComplaint.detail.appealMax',
                                            '最多 {{n}} 张',
                                            {n: MAX_APPEAL_IMAGES},
                                        )})
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 mb-2">
                                    {t(
                                        'pages.dsp.home.customerComplaint.detail.appealHintDetail',
                                        '请上传凭证，必须提供至少一张，请提供有力的证明。',
                                    )}
                                </div>
                                {isNativeWebView() ? (
                                    <>
                                        <MultiPhotoGalleryView
                                            photos={photoPreviewUrls}
                                            onDelete={handleRemovePhoto}
                                            onNativeBurstCapture={handleNativeBurstCapture}
                                            maxDisplayCount={MAX_APPEAL_IMAGES}
                                        />
                                        {selectedPhotos.length < MAX_APPEAL_IMAGES && (
                                            <div className="mt-2">
                                                <MultiPhotoSelectorView
                                                    minPhotos={0}
                                                    maxPhotos={MAX_APPEAL_IMAGES - selectedPhotos.length}
                                                    onPhotoSelected={handleNativePhotosSelected}
                                                    cameraText={t('pages.dsp.home.customerComplaint.detail.uploadEvidenceTip', '请上传申诉凭证') as string}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <Space wrap>
                                        {photoPreviewUrls.map((url, idx) => {
                                            const photo = selectedPhotos[idx];
                                            const status = photo?.status;
                                            return (
                                            <div key={photo?.uid ?? idx} className="relative">
                                                <Image
                                                    src={url}
                                                    width={64}
                                                    height={64}
                                                    fit="cover"
                                                    onClick={() => {
                                                        // 失败项点击即重传；成功 / 上传中点击预览
                                                        if (status === 'error' && photo) {
                                                            retryUpload(photo);
                                                            return;
                                                        }
                                                        setImageViewer({visible: true, images: photoPreviewUrls, index: idx});
                                                    }}
                                                />
                                                {status === 'uploading' && (
                                                    <span
                                                        className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-xs"
                                                    >
                                                        {t(
                                                            'pages.dsp.home.customerComplaint.detail.uploadingShort',
                                                            '上传中',
                                                        )}
                                                    </span>
                                                )}
                                                {status === 'error' && (
                                                    <span
                                                        className="absolute inset-0 flex items-center justify-center bg-red-600/60 text-white text-xs"
                                                    >
                                                        {t(
                                                            'pages.dsp.home.customerComplaint.detail.uploadRetryShort',
                                                            '失败·重传',
                                                        )}
                                                    </span>
                                                )}
                                                <span
                                                    className="absolute top-0 right-0 bg-black/60 text-white text-xs px-1 cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemovePhoto(idx);
                                                    }}
                                                >
                                                    ×
                                                </span>
                                            </div>
                                            );
                                        })}
                                        {selectedPhotos.length < MAX_APPEAL_IMAGES && (
                                            <div className="w-16 h-16 border border-dashed border-gray-300 rounded flex items-center justify-center">
                                                <PhotoSelector
                                                    onPhotoSelected={(file) => handlePhotoSelected(file)}
                                                    buttonText={<span className="text-2xl text-gray-400">+</span>}
                                                />
                                            </div>
                                        )}
                                    </Space>
                                )}

                                <div className="font-medium mt-3 mb-1 text-base">
                                    <span className="text-red-500 mr-1">*</span>
                                    {t('pages.dsp.home.customerComplaint.detail.remark', '备注')}
                                </div>
                                <div className="bg-gray-100 rounded p-2">
                                    <TextArea
                                        value={remark}
                                        onChange={setRemark}
                                        rows={3}
                                        maxLength={APPEAL_REMARK_MAX}
                                        showCount
                                        placeholder={t(
                                            'pages.dsp.home.customerComplaint.detail.remarkPlaceholder',
                                            '请输入',
                                        ) as string}
                                    />
                                </div>
                            </div>
                        )}

                        {!isPending && (
                            <Card
                                title={
                                    <span className="font-bold">
                                        {t('pages.dsp.home.customerComplaint.detail.appealDetail', '申诉详情')}
                                    </span>
                                }
                            >
                                {appealLoading ? (
                                    <Skeleton.Paragraph lineCount={4} animated />
                                ) : appealDetail ? (
                                    <div className="text-sm space-y-1">
                                        <div>
                                            <span className="text-gray-500">
                                                {t('pages.dsp.home.customerComplaint.detail.appealReason', '申诉理由')}：
                                            </span>
                                            <span className="text-black">{renderLocalizedText(appealDetail.appealReason, i18n.language)}</span>
                                        </div>
                                        {attachmentUrls.length > 0 && (
                                            <div>
                                                <div className="text-gray-500 mb-1">
                                                    {t(
                                                        'pages.dsp.home.customerComplaint.detail.appealAttachments',
                                                        '申诉附件',
                                                    )}：
                                                </div>
                                                <Space wrap>
                                                    {attachmentUrls.map((url, idx) => (
                                                        <Image
                                                            key={idx}
                                                            src={url}
                                                            width={80}
                                                            height={80}
                                                            fit="cover"
                                                            onClick={() =>
                                                                setImageViewer({visible: true, images: attachmentUrls, index: idx})
                                                            }
                                                        />
                                                    ))}
                                                </Space>
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-gray-500">
                                                {t('pages.dsp.home.customerComplaint.detail.appealSubmittedAt', '申诉时间')}：
                                            </span>
                                            <span className="text-black">
                                                {formatTime(appealDetail.appealSubmittedAt)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">
                                                {t('pages.dsp.home.customerComplaint.detail.reviewResult', '审核结果')}：
                                            </span>
                                            <span className="text-black">
                                                {appealDetail.reviewResult
                                                    ? t(
                                                          `pages.dsp.home.customerComplaint.reviewResult.${appealDetail.reviewResult}`,
                                                          appealDetail.reviewResult,
                                                      )
                                                    : '-'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">
                                                {t('pages.dsp.home.customerComplaint.detail.reviewedAt', '审核时间')}：
                                            </span>
                                            <span className="text-black">{formatTime(appealDetail.reviewedAt)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">
                                                {t('pages.dsp.home.customerComplaint.detail.reviewComment', '审核意见')}：
                                            </span>
                                            <span className="text-black">
                                                {renderReviewComment(appealDetail.reviewComment, i18n.language)}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-gray-500 text-sm">
                                        {t('pages.dsp.home.customerComplaint.detail.noAppeal', '暂无申诉记录')}
                                    </div>
                                )}
                            </Card>
                        )}
                    </>
                )}
            </div>

            {complaint && (isPending || isAppealing) && (
                <div className="fixed bottom-0 left-0 right-0 bg-white p-3 border-t flex gap-2 z-10">
                    {isPending && (
                        <>
                            <Button block onClick={handleAcknowledge}>
                                {t('pages.dsp.home.customerComplaint.detail.abandon', '放弃申诉')}
                            </Button>
                            <Button
                                block
                                color="primary"
                                loading={submitting}
                                disabled={selectedPhotos.length < MIN_APPEAL_IMAGES || hasUploading || isAppealExpired}
                                onClick={handleSubmitAppeal}
                            >
                                {t('pages.dsp.home.customerComplaint.detail.submitAppeal', '提交申诉')}
                            </Button>
                        </>
                    )}
                    {isAppealing && (
                        <Button block color="danger" onClick={handleAbandon}>
                            {t('pages.dsp.home.customerComplaint.detail.abandon', '放弃申诉')}
                        </Button>
                    )}
                </div>
            )}

            {imageViewer.visible && (
                <ImageViewer.Multi
                    images={imageViewer.images}
                    visible
                    defaultIndex={imageViewer.index}
                    onClose={() => setImageViewer((prev) => ({...prev, visible: false}))}
                    getContainer={null}
                />
            )}
        </div>
    );
};

export default CustomerComplaintDetailPage;
